import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildClientPostsReportQuery,
  buildTopPostsFetchKey,
  formatTopPostsReportDate,
  normalizeTopPostsPayload,
  parseTopPostsDateParts,
} from "../../lib/utils/topPostsReport"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

function topPostsArgKey(arg = {}) {
  const { clientId, startDate, endDate, sortDirection = "desc", rangeMode } = arg
  return buildTopPostsFetchKey(
    clientId,
    formatTopPostsReportDate(startDate),
    formatTopPostsReportDate(endDate),
    sortDirection === "asc" ? "asc" : "desc",
    rangeMode,
  )
}

/**
 * GET /api/v1/report/clients/{clientId}/posts
 * Month: ?aggregation_mode=&period=month&year=&month=&limit=
 * Custom range: ?aggregation_mode=&start_date=&end_date=&limit=
 */
export const fetchClientTopPosts = createAsyncThunk(
  "clientTopPosts/fetch",
  async (
    { clientId, startDate, endDate, sortDirection = "desc", rangeMode, socialNetwork } = {},
    { rejectWithValue, getState },
  ) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }

      const start = formatTopPostsReportDate(startDate)
      const end = formatTopPostsReportDate(endDate)
      const parts = parseTopPostsDateParts(end || start)
      if (!start || !end || (rangeMode !== "custom" && !parts)) {
        return rejectWithValue("startDate and endDate are required")
      }

      const direction = sortDirection === "asc" ? "asc" : "desc"
      const fetchKey = buildTopPostsFetchKey(clientId, start, end, direction, rangeMode)
      const query = buildClientPostsReportQuery(
        rangeMode === "custom"
          ? {
              period: "custom",
              startDate: start,
              endDate: end,
              aggregationMode: "paid_and_organic",
              socialNetwork,
            }
          : {
              period: "month",
              year: parts.year,
              month: parts.month,
              aggregationMode: "paid_and_organic",
              socialNetwork,
            },
      )
      const data = await get(`report/clients/${clientId}/posts?${query}`)
      const socialNetworkTypes = getState().socialNetworkTypes?.types ?? []

      return {
        raw: data,
        posts: normalizeTopPostsPayload(data, socialNetworkTypes),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        sortDirection: direction,
        fetchKey,
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch top posts")
    }
  },
  {
    condition: (arg, { getState }) => {
      const key = topPostsArgKey(arg)
      const slice = getState().clientTopPosts
      if (
        slice.status === "succeeded" &&
        String(slice.fetchedFetchKey ?? "") === key &&
        !slice.raw &&
        !(slice.posts?.length)
      ) {
        return true
      }
      return !skipIfFetchKeyLoaded(slice, key)
    },
  },
)

const initialState = {
  posts: [],
  raw: null,
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
  sortDirection: "desc",
}

function beginFetch(state, action) {
  const nextKey = topPostsArgKey(action.meta.arg || {})
  const keepStalePosts =
    nextKey === state.fetchedFetchKey && Array.isArray(state.posts) && state.posts.length > 0
  state.status = "loading"
  state.error = null
  state.requestedFetchKey = nextKey
  if (!keepStalePosts) {
    state.fetchedFetchKey = null
    state.posts = []
    state.raw = null
  }
}

function isStaleFetch(action, state) {
  return topPostsArgKey(action.meta.arg || {}) !== String(state.requestedFetchKey ?? "")
}

const clientTopPostsSlice = createSlice({
  name: "clientTopPosts",
  initialState,
  reducers: {
    clearClientTopPosts: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientTopPosts.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientTopPosts.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.raw = action.payload?.raw ?? null
        state.posts = action.payload?.posts ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.sortDirection = action.payload?.sortDirection ?? "desc"
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientTopPosts.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch top posts"
        state.posts = []
        state.raw = null
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientTopPosts } = clientTopPostsSlice.actions
export default clientTopPostsSlice.reducer
